import { APP_BASE_HREF, CommonModule } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { APP_ID, APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatSelectModule } from '@angular/material/select'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouteReuseStrategy, Router } from '@angular/router'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import * as Sentry from '@sentry/angular-ivy'
import { AuthInterceptor } from 'libs/gb-front/auth/data-access/src/lib/interceptors/auth.interceptor'
import { GbFrontSharedSocialLoginModule } from 'libs/gb-front/shared/social-login/src/lib/gb-front-shared-social-login.module'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { SharedUtilsPipesModule } from 'libs/shared/utils/pipes/src/lib/shared-utils-pipes.module'
import { SharedUtilsLoaderModule } from 'libs/shared/utils/loader/src/lib/shared-utils-loader.module'
import { environment } from '../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { NgMapsCoreModule } from '@ng-maps/core'
import { NgMapsPlacesModule } from '@ng-maps/places'
import { GOOGLE_MAPS_API_CONFIG, NgMapsGoogleModule } from '@ng-maps/google'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { appLanguageInitializerFactory } from 'libs/shared/initialization/src'
import { provideBootstrapEffects } from './core/bootstrap-effects-utils'
import { appEffects, appMetaReducers, appReducers } from './core/app-core.state'
import { EffectsModule } from '@ngrx/effects'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { StoreModule } from '@ngrx/store'
import { NgxGoogleAnalyticsModule } from 'ngx-google-analytics'
import { ErrorInterceptor } from 'libs/shared/utils/interceptors/src/lib/error.interceptor'
import { GbCurrencyPipe } from 'libs/gb-front/shared/pipes/src/lib/gb-currency-pipe/gb-currency.pipe'
import { CustomRouteReuseStrategy } from './core/custom-route-reuse-strategy'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { TableNumbersFacade } from 'libs/gb-front/auth/data-access-table-numbers/src/lib/+state/table-numbers.facade'
import { EventTicketPackagesFacade } from 'libs/gb-front/event-tickets/data-access-event-ticket-packages/src/lib/+state/event-ticket-packages.facade'
import { EventTicketPurchasesFacade } from 'libs/gb-front/event-tickets/data-access-event-ticket-purchases/src/lib/+state/event-ticket-purchases.facade'

import * as enUS from 'apps/gb-front/src/assets/i18n/en-us.json'
import * as defaultLanguage from 'apps/gb-front/src/assets/i18n/en.json'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'

function emptyFunction() {
  // do something
}

function languageInit(translate: TranslateService) {
  return appLanguageInitializerFactory(translate, defaultLanguage, enUS)
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    BrowserAnimationsModule,
    MatSnackBarModule,
    MatDialogModule,
    SharedUtilsPipesModule,
    SharedUtilsLoaderModule,
    MatTooltipModule,
    MatSelectModule,
    FontAwesomeModule,
    GbFrontSharedSocialLoginModule,
    AppRoutingModule,
    NgMapsCoreModule,
    NgMapsPlacesModule,
    NgMapsGoogleModule,
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsMeasurementId),
    TranslateModule.forRoot(),
    //ngrx
    StoreModule.forRoot(appReducers, {
      metaReducers: appMetaReducers,
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true,
      },
    }),
    EffectsModule.forRoot(appEffects),
    environment.production
      ? []
      : StoreDevtoolsModule.instrument({
          name: 'gb-front project Started',
          maxAge: 25, // Retains last 25 states
          logOnly: environment.production, // Restrict extension to log-only mode
          autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        }),
  ],
  providers: [
    provideBootstrapEffects(appEffects),
    {
      provide: APP_INITIALIZER,
      useFactory: languageInit,
      deps: [TranslateService],
      multi: true,
    },
    { provide: APP_ID, useValue: 'gb-front' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    { provide: APP_CONFIG, useValue: environment },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: MatDialogRef,
      useValue: {},
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => emptyFunction,
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useValue: environment.appBaseHref,
    },
    {
      provide: GOOGLE_MAPS_API_CONFIG,
      useValue: {
        libraries: ['places'],
        apiKey: environment.googleMapsKey,
      },
    },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    GbCurrencyPipe,
    provideHttpClient(withInterceptorsFromDi()),
    // provide facades for shared lib injection
    { provide: 'TableNumbersFacade', useClass: TableNumbersFacade },
    { provide: 'AuctionFacade', useClass: AuctionFacade },
    { provide: 'EventTicketPurchasesFacade', useClass: EventTicketPurchasesFacade },
    { provide: 'EventTicketPackagesFacade', useClass: EventTicketPackagesFacade },
    { provide: 'SelectedProfileFacade', useClass: SelectedProfileFacade },
  ],
})
export class AppModule {}
